import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Slider,
  Select,
  Button,
  Spin,
  Alert,
  Typography,
  Statistic,
  Progress,
  Tag,
  Table,
  Tabs,
  Space,
  Tooltip,
  Empty,
  message,
  InputNumber,
  Switch,
  Divider
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  CalculatorOutlined,
  ExperimentOutlined,
  BulbOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  MaterialUtilizationProjections,
  InventoryROIAnalysis,
  PROJECTION_HORIZONS,
  MARKET_CONDITIONS
} from '../../utils/projectionAnalytics';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

/**
 * Utilization Projections Component
 * Interactive what-if analysis for material utilization and ROI projections
 */
const UtilizationProjections = ({ analyticsData, loading }) => {
  // State management
  const [projectionData, setProjectionData] = useState(null);
  const [roiData, setROIData] = useState(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('utilization');
  
  // Projection parameters
  const [utilizationChanges, setUtilizationChanges] = useState([10, 20, 30, -10, -20]);
  const [timeHorizon, setTimeHorizon] = useState(PROJECTION_HORIZONS.ANNUAL);
  const [marketCondition, setMarketCondition] = useState(MARKET_CONDITIONS.STABLE);
  const [customUtilizationChange, setCustomUtilizationChange] = useState(15);
  const [investmentAmounts, setInvestmentAmounts] = useState([50000, 100000, 200000, 500000]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Generate projections when analytics data changes
  useEffect(() => {
    if (analyticsData && !loading) {
      generateProjections();
    }
  }, [analyticsData, loading, utilizationChanges, timeHorizon, marketCondition]);

  /**
   * Generate utilization and ROI projections
   */
  const generateProjections = useCallback(async () => {
    if (!analyticsData) return;

    setProjectionLoading(true);
    try {
      console.log('📊 Generating utilization projections...');

      // Generate utilization projections
      const utilizationProjections = new MaterialUtilizationProjections(analyticsData);
      const projections = utilizationProjections.generateUtilizationProjections(
        utilizationChanges,
        timeHorizon,
        marketCondition
      );

      // Generate ROI analysis
      const roiAnalysis = new InventoryROIAnalysis(analyticsData);
      const roiProjections = roiAnalysis.generateROIAnalysis(investmentAmounts, timeHorizon);

      setProjectionData(projections);
      setROIData(roiProjections);

      console.log('✅ Projections generated successfully');
    } catch (error) {
      console.error('❌ Error generating projections:', error);
      message.error('Failed to generate projections');
    } finally {
      setProjectionLoading(false);
    }
  }, [analyticsData, utilizationChanges, timeHorizon, marketCondition, investmentAmounts]);

  /**
   * Handle utilization change slider
   */
  const handleUtilizationSliderChange = useCallback((values) => {
    setUtilizationChanges(values);
  }, []);

  /**
   * Add custom utilization scenario
   */
  const addCustomScenario = useCallback(() => {
    if (!utilizationChanges.includes(customUtilizationChange)) {
      setUtilizationChanges([...utilizationChanges, customUtilizationChange]);
    }
  }, [utilizationChanges, customUtilizationChange]);

  /**
   * Get feasibility color
   */
  const getFeasibilityColor = (level) => {
    switch (level) {
      case 'High': return '#52c41a';
      case 'Medium': return '#faad14';
      case 'Low': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  /**
   * Get ROI color
   */
  const getROIColor = (roi) => {
    if (roi > 30) return '#52c41a';
    if (roi > 15) return '#faad14';
    if (roi > 0) return '#1890ff';
    return '#ff4d4f';
  };

  /**
   * Render utilization projections tab
   */
  const renderUtilizationProjections = () => {
    if (!projectionData) return null;

    const { baseline, projections, optimalScenario, recommendations, riskAnalysis } = projectionData;

    // Prepare table data
    const tableData = [
      {
        key: 'baseline',
        scenario: baseline.scenario,
        utilizationRate: baseline.utilizationRate,
        availabilityRate: baseline.availabilityRate,
        projectedRevenue: baseline.revenue,
        revenueIncrease: 0,
        revenueIncreasePercent: 0,
        roi: 0,
        feasibility: { level: 'Current', score: 100 },
        isBaseline: true
      },
      ...projections.map((proj, index) => ({
        key: `projection-${index}`,
        ...proj,
        isBaseline: false
      }))
    ];

    const columns = [
      {
        title: 'Scenario',
        dataIndex: 'scenario',
        key: 'scenario',
        render: (text, record) => (
          <div>
            <Text strong={record.isBaseline}>{text}</Text>
            {record.isBaseline && <Tag color="blue" size="small" style={{ marginLeft: 8 }}>Current</Tag>}
            {record === optimalScenario && <Tag color="gold" size="small" style={{ marginLeft: 8 }}>Optimal</Tag>}
          </div>
        )
      },
      {
        title: 'Utilization Rate',
        dataIndex: 'utilizationRate',
        key: 'utilizationRate',
        render: (value) => (
          <Progress 
            percent={value} 
            size="small"
            strokeColor={value > 80 ? '#faad14' : '#52c41a'}
            format={(percent) => `${percent.toFixed(1)}%`}
          />
        )
      },
      {
        title: 'Availability Rate',
        dataIndex: 'availabilityRate',
        key: 'availabilityRate',
        render: (value) => (
          <Progress 
            percent={value} 
            size="small"
            strokeColor={value < 30 ? '#ff4d4f' : '#52c41a'}
            format={(percent) => `${percent.toFixed(1)}%`}
          />
        )
      },
      {
        title: 'Projected Revenue',
        dataIndex: 'projectedRevenue',
        key: 'projectedRevenue',
        render: (value) => <Text strong>${value.toLocaleString()}</Text>
      },
      {
        title: 'Revenue Change',
        dataIndex: 'revenueIncrease',
        key: 'revenueIncrease',
        render: (value, record) => (
          <div>
            <Text style={{ color: value >= 0 ? '#52c41a' : '#ff4d4f' }}>
              {value >= 0 ? '+' : ''}${value.toLocaleString()}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({record.revenueIncreasePercent >= 0 ? '+' : ''}{record.revenueIncreasePercent.toFixed(1)}%)
            </Text>
          </div>
        )
      },
      {
        title: 'ROI',
        dataIndex: 'roi',
        key: 'roi',
        render: (value) => (
          <Text style={{ color: getROIColor(value) }}>
            {value > 0 ? `${value.toFixed(1)}%` : 'N/A'}
          </Text>
        )
      },
      {
        title: 'Feasibility',
        dataIndex: 'feasibility',
        key: 'feasibility',
        render: (feasibility) => (
          <Tooltip title={feasibility.issues?.join(', ') || 'No issues identified'}>
            <Tag color={getFeasibilityColor(feasibility.level)}>
              {feasibility.level}
            </Tag>
          </Tooltip>
        )
      }
    ];

    return (
      <div>
        {/* Controls */}
        <Card title="Projection Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div>
                <Text strong>Time Horizon:</Text>
                <Select
                  value={timeHorizon}
                  onChange={setTimeHorizon}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  <Option value={PROJECTION_HORIZONS.QUARTERLY}>Quarterly (3 months)</Option>
                  <Option value={PROJECTION_HORIZONS.SEMI_ANNUAL}>Semi-Annual (6 months)</Option>
                  <Option value={PROJECTION_HORIZONS.ANNUAL}>Annual (12 months)</Option>
                </Select>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div>
                <Text strong>Market Condition:</Text>
                <Select
                  value={marketCondition}
                  onChange={setMarketCondition}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  <Option value={MARKET_CONDITIONS.RECESSION}>Recession (-15%)</Option>
                  <Option value={MARKET_CONDITIONS.STABLE}>Stable (0%)</Option>
                  <Option value={MARKET_CONDITIONS.GROWTH}>Growth (+15%)</Option>
                  <Option value={MARKET_CONDITIONS.BOOM}>Boom (+30%)</Option>
                </Select>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div>
                <Text strong>Advanced Options:</Text>
                <br />
                <Switch
                  checked={showAdvancedOptions}
                  onChange={setShowAdvancedOptions}
                  style={{ marginTop: 4 }}
                />
                <Text style={{ marginLeft: 8 }}>Show Advanced</Text>
              </div>
            </Col>
          </Row>

          {showAdvancedOptions && (
            <>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <div>
                    <Text strong>Custom Utilization Change (%):</Text>
                    <Space style={{ width: '100%', marginTop: 4 }}>
                      <InputNumber
                        value={customUtilizationChange}
                        onChange={setCustomUtilizationChange}
                        min={-50}
                        max={50}
                        style={{ width: 120 }}
                      />
                      <Button onClick={addCustomScenario} size="small">
                        Add Scenario
                      </Button>
                    </Space>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div>
                    <Text strong>Active Scenarios:</Text>
                    <div style={{ marginTop: 4 }}>
                      {utilizationChanges.map((change, index) => (
                        <Tag
                          key={index}
                          closable={utilizationChanges.length > 1}
                          onClose={() => {
                            const newChanges = utilizationChanges.filter((_, i) => i !== index);
                            setUtilizationChanges(newChanges);
                          }}
                          style={{ marginBottom: 4 }}
                        >
                          {change > 0 ? '+' : ''}{change}%
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Card>

        {/* Key Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Current Utilization"
                value={baseline.utilizationRate}
                suffix="%"
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Optimal Scenario"
                value={optimalScenario.scenario}
                prefix={<BulbOutlined />}
                valueStyle={{ color: '#52c41a', fontSize: '16px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Best ROI"
                value={optimalScenario.roi}
                suffix="%"
                precision={1}
                prefix={<RiseOutlined />}
                valueStyle={{ color: getROIColor(optimalScenario.roi) }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Revenue Potential"
                value={optimalScenario.revenueIncrease}
                prefix="$"
                formatter={(value) => value.toLocaleString()}
                valueStyle={{ color: optimalScenario.revenueIncrease >= 0 ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Projections Table */}
        <Card title="Utilization Scenarios" style={{ marginBottom: 24 }}>
          <Table
            dataSource={tableData}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
        </Card>

        {/* Recommendations */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Recommendations" size="small">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <Alert
                    key={index}
                    message={rec.title}
                    description={rec.description}
                    type={rec.priority === 'high' ? 'warning' : 'info'}
                    showIcon
                    style={{ marginBottom: 8 }}
                    action={
                      <Space>
                        <Tag color={rec.priority === 'high' ? 'red' : 'blue'} size="small">
                          {rec.priority.toUpperCase()}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {rec.timeframe}
                        </Text>
                      </Space>
                    }
                  />
                ))
              ) : (
                <Empty description="No recommendations available" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Risk Analysis" size="small">
              <div style={{ marginBottom: 16 }}>
                <Text strong>Risk Distribution:</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color="green">Low Risk: {riskAnalysis.riskDistribution.low}</Tag>
                  <Tag color="orange">Medium Risk: {riskAnalysis.riskDistribution.medium}</Tag>
                  <Tag color="red">High Risk: {riskAnalysis.riskDistribution.high}</Tag>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Key Risk Factors:</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {riskAnalysis.riskFactors.map((factor, index) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <Text strong>Mitigation Strategies:</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {riskAnalysis.mitigation.map((strategy, index) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                      {strategy}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  /**
   * Render ROI analysis tab
   */
  const renderROIAnalysis = () => {
    if (!roiData) return null;

    const { analyses, recommendations, breakEvenAnalysis, riskAssessment } = roiData;

    // Prepare ROI table data
    const roiTableData = analyses.map((analysis, index) => ({
      key: `roi-${index}`,
      ...analysis
    }));

    const roiColumns = [
      {
        title: 'Investment Amount',
        dataIndex: 'investmentAmount',
        key: 'investmentAmount',
        render: (value) => <Text strong>${value.toLocaleString()}</Text>
      },
      {
        title: 'Additional Items',
        dataIndex: 'additionalItems',
        key: 'additionalItems',
        render: (value) => <Text>{value} items</Text>
      },
      {
        title: 'Capacity Increase',
        dataIndex: 'capacityIncrease',
        key: 'capacityIncrease',
        render: (value) => (
          <Progress 
            percent={value} 
            size="small"
            strokeColor="#1890ff"
            format={(percent) => `+${percent.toFixed(1)}%`}
          />
        )
      },
      {
        title: 'Revenue Increase',
        dataIndex: 'revenueIncrease',
        key: 'revenueIncrease',
        render: (value) => <Text style={{ color: '#52c41a' }}>${value.toLocaleString()}</Text>
      },
      {
        title: 'ROI',
        dataIndex: 'roi',
        key: 'roi',
        render: (value) => (
          <Text style={{ color: getROIColor(value) }}>
            {value.toFixed(1)}%
          </Text>
        )
      },
      {
        title: 'Payback Period',
        dataIndex: 'paybackMonths',
        key: 'paybackMonths',
        render: (value) => (
          <Text>
            {value === Infinity ? 'N/A' : `${value.toFixed(0)} months`}
          </Text>
        )
      },
      {
        title: 'Feasibility',
        dataIndex: 'feasibility',
        key: 'feasibility',
        render: (feasibility) => (
          <Tooltip title={feasibility.issues?.join(', ') || 'No issues identified'}>
            <Tag color={getFeasibilityColor(feasibility.level)}>
              {feasibility.level}
            </Tag>
          </Tooltip>
        )
      }
    ];

    return (
      <div>
        {/* Investment Controls */}
        <Card title="Investment Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div>
                <Text strong>Investment Amounts ($):</Text>
                <div style={{ marginTop: 8 }}>
                  {investmentAmounts.map((amount, index) => (
                    <Tag
                      key={index}
                      closable={investmentAmounts.length > 1}
                      onClose={() => {
                        const newAmounts = investmentAmounts.filter((_, i) => i !== index);
                        setInvestmentAmounts(newAmounts);
                      }}
                      style={{ marginBottom: 4 }}
                    >
                      ${amount.toLocaleString()}
                    </Tag>
                  ))}
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div>
                <Text strong>Add Custom Investment:</Text>
                <Space style={{ width: '100%', marginTop: 4 }}>
                  <InputNumber
                    placeholder="Amount"
                    min={10000}
                    max={2000000}
                    step={10000}
                    style={{ width: 150 }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    onPressEnter={(e) => {
                      const value = parseInt(e.target.value.replace(/\$\s?|(,*)/g, ''));
                      if (value && !investmentAmounts.includes(value)) {
                        setInvestmentAmounts([...investmentAmounts, value].sort((a, b) => a - b));
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      const input = document.querySelector('input[placeholder="Amount"]');
                      const value = parseInt(input.value.replace(/\$\s?|(,*)/g, ''));
                      if (value && !investmentAmounts.includes(value)) {
                        setInvestmentAmounts([...investmentAmounts, value].sort((a, b) => a - b));
                        input.value = '';
                      }
                    }}
                    size="small"
                  >
                    Add
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {/* ROI Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Best ROI"
                value={Math.max(...analyses.map(a => a.roi))}
                suffix="%"
                precision={1}
                prefix={<RiseOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Shortest Payback"
                value={Math.min(...analyses.map(a => a.paybackMonths).filter(p => p !== Infinity))}
                suffix=" months"
                precision={0}
                prefix={<CalculatorOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Max Revenue Increase"
                value={Math.max(...analyses.map(a => a.revenueIncrease))}
                prefix="$"
                formatter={(value) => value.toLocaleString()}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Risk Level"
                value={riskAssessment.overallRisk}
                prefix={<WarningOutlined />}
                valueStyle={{ 
                  color: riskAssessment.overallRisk === 'Low' ? '#52c41a' : 
                         riskAssessment.overallRisk === 'Medium' ? '#faad14' : '#ff4d4f',
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* ROI Analysis Table */}
        <Card title="Investment Analysis" style={{ marginBottom: 24 }}>
          <Table
            dataSource={roiTableData}
            columns={roiColumns}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
        </Card>

        {/* Investment Recommendations */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Investment Recommendations" size="small">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <Alert
                    key={index}
                    message={rec.title}
                    description={rec.description}
                    type={rec.priority === 'high' ? 'success' : 'info'}
                    showIcon
                    style={{ marginBottom: 8 }}
                    action={
                      <Space>
                        <Tag color={rec.priority === 'high' ? 'green' : 'blue'} size="small">
                          {rec.priority.toUpperCase()}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {rec.timeframe}
                        </Text>
                      </Space>
                    }
                  />
                ))
              ) : (
                <Empty description="No investment recommendations available" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Risk Assessment" size="small">
              <div style={{ marginBottom: 16 }}>
                <Text strong>Overall Risk: </Text>
                <Tag color={
                  riskAssessment.overallRisk === 'Low' ? 'green' : 
                  riskAssessment.overallRisk === 'Medium' ? 'orange' : 'red'
                }>
                  {riskAssessment.overallRisk}
                </Tag>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Risk Factors:</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {riskAssessment.riskFactors.map((factor, index) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: 4 }}>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <Text strong>Recommendation:</Text>
                <Paragraph style={{ fontSize: '12px', marginTop: 8 }}>
                  {riskAssessment.recommendation}
                </Paragraph>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Loading state
  if (loading || projectionLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Generating projections...</Text>
        </div>
      </div>
    );
  }

  // No data state
  if (!analyticsData) {
    return (
      <Card>
        <Empty
          description="No analytics data available for projections"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          Utilization Projections & ROI Analysis
        </Title>
        <Paragraph>
          Interactive what-if analysis to project revenue impact from utilization changes and inventory investments
        </Paragraph>
      </div>

      {/* Main Content */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <span>
                <LineChartOutlined />
                Utilization Projections
              </span>
            } 
            key="utilization"
          >
            {renderUtilizationProjections()}
          </TabPane>
          <TabPane 
            tab={
              <span>
                <DollarOutlined />
                ROI Analysis
              </span>
            } 
            key="roi"
          >
            {renderROIAnalysis()}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default UtilizationProjections;
